import ContentPasteGoIcon from '@mui/icons-material/ContentPasteGo';
import Grid from "@mui/material/Grid2";
import HoverIconButton from "../../ReusedComponents/HoverIconButton";
import { CircularProgress, Box, Paper, useTheme } from "@mui/material";
import { useState } from "react";
import {
  DataGridPro,
  GridToolbar,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarContainer,
  GridToolbarQuickFilter,
  useGridApiRef,
  GridToolbarExport,
} from "@mui/x-data-grid-pro";
// import CustomFooter from "./CustomFooter";

export default function MaterialUiDataGridPro(props) {
  const theme = useTheme()
  const { palette = {} } = theme;
  const { primary = {}, success = {}, background = {}, text = {}, divider, mode } = palette;
  const isDark = mode === "dark";
  
  // Brand colors
  const navyColor = primary?.main || "#002843";
  const greenColor = success?.main || "#A0C46D";
  const greenLight = isDark ? "rgba(160, 196, 109, 0.12)" : "rgba(160, 196, 109, 0.08)";
  const greenLightHover = isDark ? "rgba(160, 196, 109, 0.18)" : "rgba(160, 196, 109, 0.12)";
  const bgPaper = background?.paper || (isDark ? "#032d42" : "#ffffff");
  const textPrimary = text?.primary || (isDark ? "#ffffff" : "#1e293b");
  const textSecondary = text?.secondary || (isDark ? "#94a3b8" : "#64748b");
  const borderColor = divider || (isDark ? "rgba(148, 163, 184, 0.2)" : "#e2e8f0");

  const classes = {
    container: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: "16px 20px",
      backgroundColor: bgPaper,
      borderBottom: `1px solid ${borderColor}`,
      borderRadius: "12px 12px 0 0",
      flexWrap: "wrap",
      gap: "12px",
    },
    toolbarDataContainer: {
      display: "flex",
      alignItems: "center",
      color: textSecondary,
      gap: "10px",
    },
    toolbarData: {
      fontSize: "0.875rem",
      "& .MuiButton-root": {
        borderRadius: "8px",
        padding: "8px 14px",
        textTransform: "none",
        fontWeight: 500,
        backgroundColor: isDark ? "rgba(148, 163, 184, 0.08)" : "#f8fafc",
        color: isDark ? textSecondary : "#475569",
        border: `1px solid ${borderColor}`,
        transition: "all 0.2s ease-in-out",
        "&:hover": {
          backgroundColor: greenLight,
          borderColor: greenColor,
          color: isDark ? greenColor : "#7a9c4a",
        },
      },
    },
  }

  // Premium DataGrid styles
  const premiumStyles = {
    // Container styling
    backgroundColor: bgPaper,
    borderRadius: "0 0 12px 12px",
    border: `1px solid ${borderColor}`,
    boxShadow: isDark ? "0 2px 12px rgba(0, 0, 0, 0.15)" : "0 2px 12px rgba(0, 0, 0, 0.06)",
    overflow: "visible",
    
    // Cell hover effect
    "& .MuiDataGrid-cell:hover": {
      color: isDark ? greenColor : navyColor,
    },
    
    // Cell styling
    "& .MuiDataGrid-cell": {
      borderBottom: `1px solid ${isDark ? "rgba(148, 163, 184, 0.1)" : "#f1f5f9"}`,
      fontSize: "0.875rem",
      padding: "14px 16px",
      color: textPrimary,
    },

    // Row styling
    "& .MuiDataGrid-row": {
      transition: "background-color 0.15s ease-in-out",
      "&:hover": {
        backgroundColor: greenLight,
      },
      "&.Mui-selected": {
        backgroundColor: greenLightHover,
        "&:hover": {
          backgroundColor: isDark ? "rgba(160, 196, 109, 0.22)" : "rgba(160, 196, 109, 0.18)",
        },
      },
    },
    
    // Alternating row colors
    "& .MuiDataGrid-row:nth-of-type(even)": {
      backgroundColor: isDark ? "rgba(255, 255, 255, 0.02)" : "#f8fafc",
      "&:hover": {
        backgroundColor: greenLight,
      },
    },

    // Header styling
    "& .MuiDataGrid-columnHeader, .MuiDataGrid-columnHeaders": {
      borderBottom: '0 !important',
      backgroundColor: isDark ? "#002843" : navyColor,
      color: "white",
      fontWeight: 600,
      fontSize: "0.8rem",
      textTransform: "uppercase",
      letterSpacing: "0.5px",
    },
    
    // Header container
    "& .MuiDataGrid-columnHeaders": {
      borderRadius: "0",
    },

    // Checkbox color in header
    "& .MuiDataGrid-columnHeader .MuiCheckbox-root": {
      color: "rgba(255,255,255,0.8)",
      "&.Mui-checked": {
        color: greenColor,
      },
      "&.MuiCheckbox-indeterminate": {
        color: greenColor,
      },
    },
    
    // Checkbox color in rows
    "& .MuiDataGrid-cell .MuiCheckbox-root": {
      color: isDark ? "rgba(255,255,255,0.5)" : textSecondary,
      "&.Mui-checked": {
        color: greenColor,
      },
      "&:hover": {
        backgroundColor: isDark ? "rgba(160, 196, 109, 0.15)" : "rgba(160, 196, 109, 0.08)",
      },
    },

    // Header title styling
    "& .MuiDataGrid-columnHeaderTitle": {
      fontWeight: 600,
    },

    // Sort icon
    '& .MuiDataGrid-sortIcon': {
      color: 'rgba(255,255,255,0.9)',
      fontWeight: "bold",
    },
    
    // Column menu icon
    '& .MuiDataGrid-menuIconButton': {
      color: 'rgba(255,255,255,0.8)',
      "&:hover": {
        backgroundColor: "rgba(255,255,255,0.1)",
      },
    },
    
    // Footer styling
    "& .MuiDataGrid-footerContainer": {
      backgroundColor: bgPaper,
      borderTop: `1px solid ${borderColor}`,
      borderRadius: "0 0 12px 12px",
      padding: "12px 20px",
      minHeight: "60px",
    },
    
    // Pagination styling
    "& .MuiTablePagination-root": {
      color: isDark ? textSecondary : "#475569",
      fontSize: "0.875rem",
      fontWeight: 500,
    },
    
    // Pagination select
    "& .MuiTablePagination-select": {
      backgroundColor: isDark ? "rgba(148, 163, 184, 0.08)" : "#f8fafc",
      borderRadius: "8px",
      padding: "8px 14px",
      paddingRight: "32px !important",
      border: `1px solid ${borderColor}`,
      transition: "all 0.2s ease-in-out",
      fontWeight: 500,
      color: textPrimary,
      "&:hover": {
        backgroundColor: isDark ? "rgba(148, 163, 184, 0.12)" : "#f1f5f9",
        borderColor: greenColor,
      },
    },
    
    // Pagination actions (arrows)
    "& .MuiTablePagination-actions": {
      marginLeft: "16px",
      "& .MuiIconButton-root": {
        backgroundColor: isDark ? "rgba(148, 163, 184, 0.08)" : "#f8fafc",
        borderRadius: "8px",
        padding: "8px",
        margin: "0 4px",
        border: `1px solid ${borderColor}`,
        color: textSecondary,
        transition: "all 0.2s ease-in-out",
        "&:hover": {
          backgroundColor: greenColor,
          borderColor: greenColor,
          color: "#fff",
        },
        "&.Mui-disabled": {
          backgroundColor: isDark ? "rgba(148, 163, 184, 0.05)" : "#f8fafc",
          borderColor: borderColor,
          color: isDark ? "#64748b" : "#cbd5e1",
        },
      },
    },
    
    // Column separator
    "& .MuiDataGrid-columnSeparator": {
      color: "rgba(255,255,255,0.2)",
    },
    
    // Loading overlay
    "& .MuiDataGrid-overlay": {
      backgroundColor: isDark ? "rgba(0, 20, 31, 0.9)" : "rgba(248, 250, 252, 0.9)",
    },
    
    // Hide footer selected row count (shown in toolbar instead)
    "& .MuiDataGrid-selectedRowCount": {
      display: "none !important",
    },
  };

  const {
    autoHeight = true,
    checkboxSelection = true,
    columns = [],
    customComponents = {},
    customFilters = null,
    customGridToolbar = null,
    disableColumnResize = false,
    disableColumnFilter = false,
    disableColumnSelector = false,
    disableExportOption = true,
    disableSelectionOnClick = true,
    disableToolbar = false,
    delayedLoad = false,
    exportItemType = "",
    exportFileName = "",
    getRowId = () => { },
    getTreeDataPath = () => { },
    handleConfirmationModal = () => { },
    handleExport = false,
    handleRowSelect = () => { },
    height = "80vh",
    hideFooter = false,
    hideFooterRowCount = false,
    loading = false,
    needsCustomToolbar = false,
    onFilterModelChange = () => { },
    onRowClick = () => { },
    onRowOrderChange = () => { },
    onSortModelChange = () => { },
    pageSize = 100,
    pagination = true,
    rowReordering = false,
    rows = [],
    selectionModel = undefined,
    showGridToolBar = false,
    state = {},
    sx = {},
    toolBarButtons = () => { },
    treeData = false,
  } = props;

  const [pgSize, setPgSize] = useState(pageSize);
  const [page, setPage] = useState(0);
  const apiRef = useGridApiRef();
  let components = {};

  // Appends the toolbar
  if (showGridToolBar) {
    // If the parent provides a custom toolbar, we will use that. Otherwise we use the default
    components.Toolbar =
      showGridToolBar && customGridToolbar
        ? customGridToolbar
        : showGridToolBar
          ? GridToolbar
          : null;
  }

  // Appends custom filters
  if (customFilters) {
    components.FilterPanel = customFilters;
  }

  // Custom toolbar with premium styling
  function CustomToolbar() {
    return (
      <GridToolbarContainer sx={classes.container}>
        <Grid sx={classes.toolbarDataContainer}>
          <Grid sx={classes.toolbarData}>
            <GridToolbarColumnsButton />
          </Grid>
          <Grid sx={classes.toolbarData}>
            <GridToolbarFilterButton />
          </Grid>
          <Grid sx={classes.toolbarData}>
            <GridToolbarExport
              csvOptions={{ disableToolbarButton: disableExportOption, fileName: exportFileName }}
              printOptions={{ disableToolbarButton: true }}
            />
          </Grid>
          <Grid sx={{
            ...classes.toolbarData,
            "& .MuiInputBase-root": {
              borderRadius: "8px",
              backgroundColor: isDark ? "rgba(148, 163, 184, 0.08)" : "#f8fafc",
              padding: "6px 14px",
              fontSize: "0.875rem",
              border: `1px solid ${borderColor}`,
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                borderColor: greenColor,
              },
              "&.Mui-focused": {
                borderColor: greenColor,
                backgroundColor: bgPaper,
                boxShadow: `0 0 0 3px ${greenLight}`,
              },
            },
            "& .MuiInputBase-input": {
              padding: "4px 0",
              color: textPrimary,
            },
            "& .MuiSvgIcon-root": {
              color: textSecondary,
            },
          }}>
            <GridToolbarQuickFilter 
              debounceMs={500}
              placeholder="Search..."
            />
          </Grid>
        </Grid>
        <Grid sx={classes.toolbarDataContainer}>
          {toolBarButtons()}
        </Grid>
        {handleExport ?
          <Grid sx={classes.toolbarDataContainer}>
            <span>
              <HoverIconButton
                downloadDocType="Excel"
                exportItemType={exportItemType}
                handleClick={() => { handleExport(state, handleConfirmationModal) }}
                icon={<ContentPasteGoIcon/>}
                iconDirection="right"
                text="Export"
              />
            </span>
          </Grid>
          : null}
      </GridToolbarContainer>
    );
  }

  // Merge premium styles with any passed sx prop
  const finalStyles = { ...premiumStyles, ...sx };

  return (
    <Paper 
      elevation={0}
      sx={{ 
        height: height,
        borderRadius: "12px",
        overflow: "hidden",
        boxShadow: isDark ? "0 4px 20px rgba(0,0,0,0.2)" : "0 4px 20px rgba(0,0,0,0.06)",
        border: `1px solid ${borderColor}`,
        backgroundColor: bgPaper,
      }}
    >
      <DataGridPro
        apiRef={apiRef}
        autoHeight={autoHeight}
        //deprecated. now slotProps
        componentsProps={{
          toolbar: {
            csvOptions: { disableToolbarButton: disableExportOption, fileName: exportFileName },
            printOptions: { disableToolbarButton: true },
            quickFilterProps: { debounceMs: 500 },
            showQuickFilter: true,
          },
        }}
        checkboxSelection={checkboxSelection}
        columns={columns}
        //deprecated. now slots
        components={{
          Toolbar: disableToolbar ? null : needsCustomToolbar ? CustomToolbar : GridToolbar,
          ...customComponents,
        }}
        disableColumnResize={disableColumnResize}
        disableSelectionOnClick={disableSelectionOnClick}
        // following 3 props are used to disable toolbar icons when custom toolbar isnt in use
        disableColumnFilter={disableColumnFilter}
        disableColumnSelector={disableColumnSelector}
        disableDensitySelector
        experimentalFeatures={{ newEditingApi: true }}
        filterMode="client"
        getRowId={getRowId}
        getTreeDataPath={getTreeDataPath}
        hideFooterRowCount={hideFooterRowCount}
        hideFooter={hideFooter}
        keepNonExistentRowsSelected
        initialState={{
          sorting: {
            sortModel: [{
              field: 'timeOfLog', sort: 'desc'
            }]
          }
        }}
        loading={loading}
        localeText={{
          noRowsLabel: delayedLoad ? <CircularProgress sx={{ color: greenColor }} /> : "No rows",
        }}
        onFilterModelChange={(newFilterModel) => {
          onFilterModelChange(newFilterModel);
        }}
        onPageChange={(newPage) => setPage(newPage)}
        onPageSizeChange={(newPageSize) => setPgSize(newPageSize)}
        onRowClick={(rowData) => onRowClick(rowData)}
        onRowOrderChange={(changeInfo) => onRowOrderChange(changeInfo)}
        onSelectionModelChange={(selectedRowIds) => {
          handleRowSelect(selectedRowIds);
        }}
        selectionModel={selectionModel}
        onSortModelChange={onSortModelChange}
        page={page}
        pageSize={pgSize}
        pagination={pagination}
        // paginationMode="client"
        // rowCount={rowCountState}
        // rowHeight={rowHeight}
        rowReordering={rowReordering}
        rows={rows}
        rowsPerPageOptions={[10, 25, 50, 100, 250]}
        sx={finalStyles}
        treeData={treeData}
      />
    </Paper>
  );
}
